import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { fetchCourses, fetchQueueItems, type CourseSummary } from '../api/client.js';
import { isMachineActor, MachineActorBadge } from './MachineActorBadge.js';
import { WorkItemTitle } from './WorkItemTitle.js';
import { readCourseFromLocation, resolveCourseSelection } from '../lib/courseSelection.js';
import { BUTTON_INTERACTION_STYLES, LINK_INTERACTION_STYLES } from '../lib/actionStyles.js';
import type { WorkItemStatus, WorkItemSummary } from '../types/workflow.js';

// Lifecycle board (AD feedback R-13): a single glance at where every work item sits as it moves
// pending → approved → cancelled → executed, with timestamps + who acted. Read-only; it visualizes
// the same review-event data the queue writes, so a reviewer can trace a whole wave through its
// stage gates instead of holding it in their head.

interface BoardColumn {
  key: string;
  label: string;
  statuses: WorkItemStatus[];
  accent: string;
  chip: string;
}

const COLUMNS: BoardColumn[] = [
  { key: 'pending', label: 'Pending', statuses: ['pending'], accent: 'border-t-blue-400', chip: 'bg-blue-50 text-blue-600' },
  { key: 'approved', label: 'Approved', statuses: ['approved', 'approved_with_edit'], accent: 'border-t-emerald-400', chip: 'bg-emerald-50 text-emerald-600' },
  { key: 'cancelled', label: 'Cancelled', statuses: ['cancelled'], accent: 'border-t-rose-400', chip: 'bg-rose-50 text-rose-500' },
  // `executing` is the transient in-flight state on the way to `executed` — same column, so an item
  // mid-execution never vanishes from the board.
  { key: 'executed', label: 'Executed', statuses: ['executing', 'executed'], accent: 'border-t-slate-400', chip: 'bg-slate-100 text-slate-600' },
  // Rendered only when non-empty (rollbacks are rare) — but never silently hidden when they exist.
  { key: 'rolled_back', label: 'Rolled back', statuses: ['rolled_back', 'rollback_failed'], accent: 'border-t-amber-400', chip: 'bg-amber-50 text-amber-700' },
];

// Every lifecycle status — the board's whole promise is that no item is invisible.
const BOARD_STATUSES: WorkItemStatus[] = [
  'pending',
  'approved',
  'approved_with_edit',
  'executing',
  'executed',
  'cancelled',
  'rolled_back',
  'rollback_failed',
];

type BoardPresetKey = 'waiting_for_ad' | 'approved_awaiting_wave' | 'recently_executed' | 'cancelled' | 'all';

const SAVED_VIEW_PRESETS: Array<{
  key: BoardPresetKey;
  label: string;
  statuses: WorkItemStatus[];
}> = [
  { key: 'waiting_for_ad', label: 'Waiting for AD', statuses: ['pending'] },
  { key: 'approved_awaiting_wave', label: 'Approved — awaiting wave', statuses: ['approved', 'approved_with_edit'] },
  { key: 'recently_executed', label: 'Recently executed', statuses: ['executed'] },
  { key: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
  { key: 'all', label: 'All', statuses: [] },
];

export function WorkQueueBoard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [courses, setCourses] = useState<CourseSummary[]>([]);
  const [items, setItems] = useState<WorkItemSummary[]>([]);
  const [truncatedStatuses, setTruncatedStatuses] = useState<Set<WorkItemStatus>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Every spelling — query `?courseKey`, the browser's legacy `?courseId`, or a `/courses/<id>/…`
  // path — then the level last chosen in this tab, then the authoring default. See
  // `lib/courseSelection.ts`.
  const { courseKey } = resolveCourseSelection(courses, readCourseFromLocation(location.pathname, searchParams));
  const sectionId = searchParams.get('sectionId') ?? undefined;
  const statusParam = searchParams.get('status');
  const activePreset = activeBoardPreset(statusParam);

  useEffect(() => {
    let cancelled = false;
    void fetchCourses()
      .then((loaded) => {
        if (!cancelled) setCourses(loaded);
      })
      .catch(() => {
        if (!cancelled) setCourses([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!courseKey) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    const statuses = boardStatusesFromParam(statusParam);
    void Promise.all(
      statuses.map((status) => fetchQueueItems({ courseKey, status, ...(sectionId ? { sectionId } : {}) })),
    )
      .then((pages) => {
        if (!cancelled) {
          setItems(pages.flatMap((page) => page.items));
          // A page with a nextCursor was cut off at the server limit — surface it ("N+") instead of
          // presenting a truncated column as the full count.
          setTruncatedStatuses(
            new Set(statuses.filter((_status, index) => Boolean(pages[index]?.nextCursor))),
          );
          setLoading(false);
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : 'Unable to load the board');
          setItems([]);
          setTruncatedStatuses(new Set());
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [courseKey, sectionId, statusParam]);

  function selectPreset(key: BoardPresetKey) {
    const preset = SAVED_VIEW_PRESETS.find((entry) => entry.key === key);
    if (!preset) return;
    const next = new URLSearchParams(searchParams);
    if (preset.statuses.length === 0) next.delete('status');
    else next.set('status', preset.statuses.join(','));
    setSearchParams(next);
  }

  /**
   * One level at a time, enforced a second time where the board renders — the same backstop as the
   * queue's, for the same reason (`WorkQueueDashboard.tsx`, `courseScoped`). The server scopes this
   * list; a foreign item arriving anyway would sit in a lifecycle column under a heading naming
   * another level, and lesson numbers repeat across levels.
   */
  const courseScopedItems = useMemo(
    () => (courseKey ? items.filter((item) => item.courseKey === courseKey) : items),
    [courseKey, items],
  );
  const foreignItemCount = items.length - courseScopedItems.length;

  const byColumn = useMemo(() => {
    return COLUMNS.map((column) => {
      const columnItems = courseScopedItems
        .filter((item) => column.statuses.includes(item.status))
        .sort((left, right) => lastActivity(right).localeCompare(lastActivity(left)));
      return {
        column,
        items: columnItems,
        truncated: column.statuses.some((status) => truncatedStatuses.has(status)),
      };
    }).filter(({ column, items: columnItems }) => column.key !== 'rolled_back' || columnItems.length > 0);
  }, [courseScopedItems, truncatedStatuses]);

  if (!courseKey) {
    return <Panel message="Select a course to load the board." />;
  }
  if (loading) {
    return <Panel message="Loading board..." pulse />;
  }
  if (error) {
    return <Panel message={error} tone="error" />;
  }

  const courseParam = `?courseKey=${encodeURIComponent(courseKey)}`;

  return (
    <main className="flex-1 overflow-y-auto bg-background-light">
      <div className="flex items-end justify-between px-6 pb-4 pt-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lifecycle Board</h1>
          <p className="text-sm text-slate-500">
            {courseLabel(courseKey, courses)} — how items move through review{sectionId ? ` · section ${sectionId}` : ''}
          </p>
          {foreignItemCount > 0 ? (
            <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="foreign-course-items-notice">
              {foreignItemCount} item{foreignItemCount === 1 ? '' : 's'} from another course
              {foreignItemCount === 1 ? ' was' : ' were'} returned for this board and
              {foreignItemCount === 1 ? ' is' : ' are'} not shown. Please report this — the board
              should never be asked about more than one course.
            </p>
          ) : null}
        </div>
        <Link className={`text-sm font-bold text-primary hover:underline ${LINK_INTERACTION_STYLES}`} to={`/queue${courseParam}`}>
          Back to queue
        </Link>
      </div>
      <div aria-label="Saved views" className="flex flex-wrap items-center gap-2 px-6 pb-4">
        <span className="mr-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Saved views</span>
        {SAVED_VIEW_PRESETS.map((preset) => {
          const selected = activePreset === preset.key;
          return (
            <button
              aria-pressed={selected}
              className={`${selected
                ? 'rounded-full border border-primary bg-primary px-3 py-1.5 text-xs font-bold text-white'
                : 'rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50'} ${BUTTON_INTERACTION_STYLES}`}
              key={preset.key}
              onClick={() => selectPreset(preset.key)}
              type="button"
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <div className={`grid grid-cols-1 gap-4 px-6 pb-12 md:grid-cols-2 ${byColumn.length > 4 ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}>
        {byColumn.map(({ column, items: columnItems, truncated }) => (
          <section className={`rounded-xl border border-t-4 border-slate-200 bg-white shadow-sm ${column.accent}`} key={column.key}>
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">{column.label}</h2>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${column.chip}`}
                title={truncated ? 'More items than the board loads at once — count is a lower bound' : undefined}
              >
                {columnItems.length}
                {truncated ? '+' : ''}
              </span>
            </div>
            <div className="flex max-h-[calc(100vh-16rem)] flex-col gap-2 overflow-y-auto p-3">
              {columnItems.length === 0 ? (
                <p className="px-1 py-6 text-center text-xs text-slate-400">Nothing here.</p>
              ) : (
                columnItems.map((item) => <BoardCard courseParam={courseParam} item={item} key={item.workItemId} />)
              )}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}

function BoardCard({ item, courseParam }: { item: WorkItemSummary; courseParam: string }) {
  const actedAt = item.reviewedAt ?? item.createdAt;
  return (
    <Link
      className={`block rounded-lg border border-slate-200 bg-white p-3 transition-colors hover:border-primary hover:bg-slate-50 ${LINK_INTERACTION_STYLES}`}
      to={`/queue${courseParam}&sectionId=${encodeURIComponent(item.sectionId)}&status=${encodeURIComponent(item.status)}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="material-symbols-outlined text-[16px] text-slate-400" title={item.op}>
          {opGlyph(item.op)}
        </span>
        <span className="line-clamp-2 text-sm font-semibold text-slate-800">
          <WorkItemTitle item={item} plainQuestionReference />
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400">
        <span className="truncate font-mono">{item.clusterKey.split(':').pop()}</span>
        <span title={actedAt}>{relativeTime(actedAt)}</span>
      </div>
      {item.reviewerId ? (
        <div className="mt-1 flex items-center gap-1 truncate text-[10px] text-slate-400">
          <span className="truncate">by {item.reviewerId}</span>
          {/* A bulk approve writes the operator's address here for every item in the run. Without
              the marker the card asserts that person reviewed this one. */}
          {isMachineActor({ actorType: item.reviewerActorType }) ? (
            <MachineActorBadge authority={item.reviewerAuthority} testId="board-card-machine-actor" />
          ) : null}
        </div>
      ) : null}
    </Link>
  );
}

export function boardStatusesFromParam(statusParam: string | null): WorkItemStatus[] {
  if (!statusParam || statusParam === 'all') return [...BOARD_STATUSES];
  const validStatuses = new Set<WorkItemStatus>(BOARD_STATUSES);
  return [...new Set(
    statusParam
      .split(',')
      .map((status) => status.trim())
      .filter((status): status is WorkItemStatus => validStatuses.has(status as WorkItemStatus)),
  )];
}

export function activeBoardPreset(statusParam: string | null): BoardPresetKey | null {
  if (!statusParam) return 'all';
  const tokens = statusParam.split(',').map((status) => status.trim()).filter(Boolean);
  for (const preset of SAVED_VIEW_PRESETS) {
    if (preset.statuses.length === 0 || tokens.length !== preset.statuses.length) continue;
    const expected = [...preset.statuses].sort();
    if ([...tokens].sort().every((status, index) => status === expected[index])) return preset.key;
  }
  return null;
}

function Panel({ message, tone = 'neutral', pulse = false }: { message: string; tone?: 'neutral' | 'error'; pulse?: boolean }) {
  return (
    <main className="flex-1 bg-background-light p-6">
      <div
        className={
          tone === 'error'
            ? 'grid min-h-[12rem] place-items-center rounded-2xl border border-rose-200 bg-rose-50 px-6 text-center text-sm text-rose-800'
            : 'grid min-h-[12rem] place-items-center rounded-2xl border border-slate-200 bg-white px-6 text-center text-sm text-slate-500'
        }
      >
        <span className={pulse ? 'animate-pulse' : ''}>{message}</span>
      </div>
    </main>
  );
}

function lastActivity(item: WorkItemSummary): string {
  return item.reviewedAt ?? item.createdAt ?? '';
}

function courseLabel(courseKey: string | undefined, courses: CourseSummary[]): string {
  if (!courseKey) {
    return 'Course';
  }
  return courses.find((course) => course.id === courseKey)?.title ?? courseKey;
}

function opGlyph(op: string): string {
  switch (op) {
    case 'create':
      return 'note_add';
    case 'fix':
      return 'account_tree';
    case 'rename':
      return 'drive_file_rename_outline';
    case 'move':
      return 'drive_file_move';
    case 'reorder':
      return 'swap_vert';
    case 'archive':
      return 'archive';
    default:
      return 'label';
  }
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const diffMs = Date.now() - then;
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) {
    return 'just now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
